import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// FIX: cada desarrollo es de un cliente distinto (a veces con su propia
// agencia de marketing) -- una línea de WhatsApp se liga a uno o varios
// desarrollos, nunca al revés. Las credenciales (app_secret, access_token)
// nunca se guardan en esta pantalla ni en la tabla wa_numeros: solo pasan
// por la Edge Function wa-guardar-credenciales, que las encripta en Vault.
function formVacio() {
  return { id: null, nombre: '', phone_number_id: '', waba_id: '', verify_token: '', app_secret: '', access_token: '', activo: true, desarrollo_ids: [] };
}

// FIX: la misma URL sirve para todas las líneas/agencias -- el webhook
// identifica cuál es cuál por el phone_number_id que manda Meta. Se arma
// con la URL real del proyecto en vez de hardcodearla, por si algún día
// cambia de entorno.
const WEBHOOK_URL = `${supabase.supabaseUrl}/functions/v1/wa-webhook`;

function CampoCopiable({ label, valor }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try { await navigator.clipboard.writeText(valor); setCopiado(true); setTimeout(() => setCopiado(false), 1500); } catch { /* noop */ }
  };
  return (
    <div style={{ marginBottom: '10px' }}>
      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>{label}</label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input readOnly value={valor} onFocus={e => e.target.select()}
          style={{ flex: 1, padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '12px', background: '#f9f9f9', color: '#555' }} />
        <button type='button' onClick={copiar}
          style={{ padding: '8px 12px', background: copiado ? '#27500A' : '#4A4A4A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {copiado ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

export default function LineasWhatsApp({ miRol }) {
  const isMobile = useIsMobile();
  const [lineas, setLineas] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [ligas, setLigas] = useState([]); // wa_numero_desarrollos completo
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(formVacio());

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    setLoading(true);
    const [{ data: dLineas }, { data: dDesarrollos }, { data: dLigas }] = await Promise.all([
      supabase.from('wa_numeros').select('*').order('nombre'),
      supabase.from('desarrollos').select('id, nombre').order('nombre'),
      supabase.from('wa_numero_desarrollos').select('*'),
    ]);
    setLineas(dLineas || []);
    setDesarrollos(dDesarrollos || []);
    setLigas(dLigas || []);
    setLoading(false);
  };

  const desarrollosDeLinea = (numeroId) => ligas.filter(l => l.wa_numero_id === numeroId).map(l => l.desarrollo_id);
  const nombreDesarrollo = (id) => desarrollos.find(d => d.id === id)?.nombre || '—';

  const abrirNueva = () => { setForm(formVacio()); setErrorMsg(''); setShowForm(true); };
  const abrirEditar = (linea) => {
    setForm({
      id: linea.id, nombre: linea.nombre, phone_number_id: linea.phone_number_id, waba_id: linea.waba_id || '',
      verify_token: linea.verify_token, app_secret: '', access_token: '', activo: linea.activo,
      desarrollo_ids: desarrollosDeLinea(linea.id),
    });
    setErrorMsg('');
    setShowForm(true);
  };

  const toggleDesarrollo = (id) => {
    setForm(f => ({ ...f, desarrollo_ids: f.desarrollo_ids.includes(id) ? f.desarrollo_ids.filter(x => x !== id) : [...f.desarrollo_ids, id] }));
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim() || !form.phone_number_id.trim() || !form.verify_token.trim()) {
      setErrorMsg('Nombre, Phone Number ID y Verify Token son obligatorios'); return;
    }
    if (!form.id && (!form.app_secret.trim() || !form.access_token.trim())) {
      setErrorMsg('App Secret y Access Token son obligatorios al dar de alta una línea nueva'); return;
    }
    setGuardando(true); setErrorMsg('');
    const { data: sesion } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke('wa-guardar-credenciales', {
      body: {
        numero_id: form.id || undefined, nombre: form.nombre.trim(), phone_number_id: form.phone_number_id.trim(),
        waba_id: form.waba_id.trim() || null, verify_token: form.verify_token.trim(),
        app_secret: form.app_secret.trim() || undefined, access_token: form.access_token.trim() || undefined,
        activo: form.activo, desarrollo_ids: form.desarrollo_ids,
      },
      headers: { Authorization: `Bearer ${sesion?.session?.access_token}` },
    });
    setGuardando(false);
    if (error || data?.error) { setErrorMsg(data?.error || error?.message || 'Error al guardar la línea'); return; }
    setShowForm(false);
    cargarTodo();
  };

  if (miRol !== 'Super Admin') {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No tienes acceso a este módulo</div>;
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Líneas de WhatsApp</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>{lineas.length} líneas · cada una ligada a su(s) desarrollo(s)</div>
        </div>
        <button onClick={abrirNueva} style={btnPrimary}>+ Nueva línea</button>
      </div>

      <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '14px 16px', marginBottom: '1rem' }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
          Dale esta URL a la agencia de cada desarrollo para que la pegue en su App de Meta → WhatsApp → Configuration → Webhook. Es la misma para todas las líneas.
        </div>
        <CampoCopiable label='URL del Webhook' valor={WEBHOOK_URL} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Cargando...</div>
      ) : lineas.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Sin líneas configuradas todavía</div>
      ) : (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
          {lineas.map((l, i) => (
            <div key={l.id} onClick={() => abrirEditar(l)} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: i < lineas.length - 1 ? '0.5px solid #f0f0f0' : 'none', cursor: 'pointer' }}>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{l.nombre}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{l.phone_number_id}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: '160px' }}>
                {desarrollosDeLinea(l.id).length === 0 ? (
                  <span style={{ fontSize: '11px', color: '#aaa' }}>Sin desarrollo ligado</span>
                ) : desarrollosDeLinea(l.id).map(id => (
                  <span key={id} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#f0f0f0', color: '#555' }}>{nombreDesarrollo(id)}</span>
                ))}
              </div>
              <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '600', background: l.activo ? '#EAF3DE' : '#FCEBEB', color: l.activo ? '#27500A' : '#A32D2D' }}>
                {l.activo ? 'Activa' : 'Inactiva'}
              </span>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setShowForm(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1a1a2e' }}>{form.id ? 'Editar línea' : 'Nueva línea de WhatsApp'}</h3>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '1rem' }}>Las credenciales se guardan encriptadas — no se muestran de vuelta.</div>

            {form.id && (
              <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: '600' }}>PARA DARLE A LA AGENCIA DE ESTA LÍNEA</div>
                <CampoCopiable label='URL del Webhook' valor={WEBHOOK_URL} />
                <CampoCopiable label='Verify Token' valor={form.verify_token} />
              </div>
            )}

            {inp('Nombre / etiqueta', 'nombre', form, setForm)}
            {inp('Phone Number ID (Meta)', 'phone_number_id', form, setForm)}
            {inp('WhatsApp Business Account ID (opcional)', 'waba_id', form, setForm)}
            {inp('Verify Token (inventa uno, va en Meta)', 'verify_token', form, setForm)}
            {inp(form.id ? 'App Secret (dejar vacío para no cambiarlo)' : 'App Secret', 'app_secret', form, setForm, 'password')}
            {inp(form.id ? 'Access Token (dejar vacío para no cambiarlo)' : 'Access Token', 'access_token', form, setForm, 'password')}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0 16px' }}>
              <input type='checkbox' checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} id='activo' />
              <label htmlFor='activo' style={{ fontSize: '13px', color: '#333' }}>Línea activa</label>
            </div>

            <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '6px' }}>Desarrollo(s) ligado(s)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px', maxHeight: '160px', overflowY: 'auto', padding: '4px' }}>
              {desarrollos.map(d => (
                <button key={d.id} type='button' onClick={() => toggleDesarrollo(d.id)}
                  style={{ padding: '5px 12px', borderRadius: '20px', border: '0.5px solid', cursor: 'pointer', fontSize: '12px', background: form.desarrollo_ids.includes(d.id) ? '#4A4A4A' : '#f5f5f5', color: form.desarrollo_ids.includes(d.id) ? '#fff' : '#666', borderColor: form.desarrollo_ids.includes(d.id) ? '#4A4A4A' : '#ddd' }}>
                  {d.nombre}
                </button>
              ))}
            </div>

            {errorMsg && <div style={{ padding: '10px', background: '#FCEBEB', color: '#A32D2D', borderRadius: '8px', fontSize: '12px', marginBottom: '14px' }}>{errorMsg}</div>}

            <button onClick={handleGuardar} disabled={guardando} style={{ ...btnPrimary, width: '100%', padding: '12px', justifyContent: 'center', fontSize: '14px' }}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = (label, key, form, setForm, type = 'text') => (
  <div style={{ marginBottom: '12px' }}>
    <label style={labelStyle}>{label}</label>
    <input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
  </div>
);

const btnPrimary = { background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' };
const labelStyle = { fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' };
